import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { dashboardAPI, jobsAPI, deliveriesAPI } from "../lib/api";
import { formatDate, formatDateTime } from "../lib/dateUtils";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Wrench,
  Users,
  Truck,
  Package,
  FileText,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  Eye,
  MapPin,
  TruckIcon,
} from "lucide-react";

const Dashboard = () => {
  const { user, hasPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  const isMechanic = user?.role === "mechanic";
  const isDriver = user?.role === "driver";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [dashboardAPI.getStats()];
        
        if (isMechanic) {
          promises.push(jobsAPI.getAll());
        } else {
          promises.push(Promise.resolve({ data: { items: [] } }));
        }
        
        if (isDriver) {
          promises.push(deliveriesAPI.getAll());
        } else {
          promises.push(Promise.resolve({ data: { items: [] } }));
        }
        
        const [statsRes, jobsRes, deliveriesRes] = await Promise.all(promises);
        setStats(statsRes.data);
        // Handle paginated response - extract items array
        setJobs(jobsRes.data?.items || jobsRes.data || []);
        setDeliveries(deliveriesRes.data?.items || deliveriesRes.data || []);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isMechanic, isDriver]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card className="border-slate-200 hover:border-slate-400 transition-colors">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">
              {title}
            </p>
            <p className="font-heading text-3xl font-bold text-slate-900">{value}</p>
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-sm ${color}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-800 border-amber-300",
      in_progress: "bg-blue-100 text-blue-800 border-blue-300",
      completed: "bg-green-100 text-green-800 border-green-300",
    };
    return (
      <Badge className={`${styles[status] || styles.pending} rounded-none uppercase text-xs font-bold tracking-wide border`}>
        {status?.replace("_", " ")}
      </Badge>
    );
  };

  // Driver Dashboard
  if (isDriver) {
    const pendingDeliveries = deliveries.filter((d) => d.status === "pending" && !d.is_expired);
    const expiredDeliveries = deliveries.filter((d) => d.status === "pending" && d.is_expired);
    const inProgressDeliveries = deliveries.filter((d) => d.status === "in_progress");
    const completedDeliveries = deliveries.filter((d) => d.status === "completed");

    return (
      <div data-testid="driver-dashboard" className="space-y-6">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            My Dashboard
          </h1>
          <p className="text-slate-500">
            Welcome back, <span className="font-semibold">{user?.name}</span>
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <StatCard
            title="Pending Deliveries"
            value={pendingDeliveries.length}
            icon={Clock}
            color="bg-amber-100 text-amber-700"
          />
          <StatCard
            title="Expired"
            value={expiredDeliveries.length}
            icon={AlertTriangle}
            color="bg-red-100 text-red-700"
          />
          <StatCard
            title="In Progress"
            value={inProgressDeliveries.length}
            icon={TruckIcon}
            color="bg-blue-100 text-blue-700"
          />
          <StatCard
            title="Completed"
            value={completedDeliveries.length}
            icon={CheckCircle}
            color="bg-green-100 text-green-700"
          />
        </div>

        {/* Pending Deliveries */}
        {pendingDeliveries.length > 0 && (
          <Card className="border-amber-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg font-heading uppercase">
                <Clock className="w-5 h-5 text-amber-500" />
                Pending Deliveries ({pendingDeliveries.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingDeliveries.map((delivery) => (
                  <div
                    key={delivery.id}
                    className="p-4 bg-amber-50 border border-amber-200 rounded-sm"
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <p className="font-semibold text-slate-900">
                          {delivery.customer_name}
                        </p>
                        <p className="text-sm text-slate-600">
                          {delivery.excavator_model} - {delivery.excavator_serial}
                        </p>
                        {(delivery.scheduled_date || delivery.scheduled_time) && (
                          <p className="text-xs text-amber-700 font-medium flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {delivery.scheduled_date && formatDate(delivery.scheduled_date)}
                            {delivery.scheduled_time && (
                              <span className="font-bold">
                                @ {delivery.scheduled_time.slice(0, 2)}:{delivery.scheduled_time.slice(2)}
                              </span>
                            )}
                          </p>
                        )}
                        {delivery.pickup_location && (
                          <p className="text-xs text-green-700 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> From: {delivery.pickup_location}
                          </p>
                        )}
                        {delivery.delivery_location && (
                          <p className="text-xs text-red-700 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> To: {delivery.delivery_location}
                          </p>
                        )}
                      </div>
                      <Link to={`/deliveries/${delivery.id}`}>
                        <Button
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Start
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* In Progress Deliveries */}
        {inProgressDeliveries.length > 0 && (
          <Card className="border-blue-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg font-heading uppercase">
                <TruckIcon className="w-5 h-5 text-blue-500" />
                In Progress ({inProgressDeliveries.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {inProgressDeliveries.map((delivery) => (
                  <div
                    key={delivery.id}
                    className="p-4 bg-blue-50 border border-blue-200 rounded-sm"
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <p className="font-semibold text-slate-900">
                          {delivery.customer_name}
                        </p>
                        <p className="text-sm text-slate-600">
                          {delivery.excavator_model} - {delivery.excavator_serial}
                        </p>
                        {delivery.delivery_location && (
                          <p className="text-xs text-red-700 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Deliver To: {delivery.delivery_location}
                          </p>
                        )}
                      </div>
                      <Link to={`/deliveries/${delivery.id}`}>
                        <Button
                          size="sm"
                          className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Continue
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completed Deliveries */}
        {completedDeliveries.length > 0 && (
          <Card className="border-green-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg font-heading uppercase">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Completed ({completedDeliveries.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {completedDeliveries.slice(0, 5).map((delivery) => (
                  <div
                    key={delivery.id}
                    className="p-4 bg-green-50 border border-green-200 rounded-sm"
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <p className="font-semibold text-slate-900">
                          {delivery.customer_name}
                        </p>
                        <p className="text-sm text-slate-600">
                          {delivery.excavator_model} - {delivery.excavator_serial}
                        </p>
                      </div>
                      <Link to={`/deliveries/${delivery.id}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-sm border-green-300"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {deliveries.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <TruckIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No deliveries assigned to you yet.</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Mechanic Dashboard
  if (isMechanic) {
    const pendingJobs = jobs.filter((j) => j.status === "pending");
    const inProgressJobs = jobs.filter((j) => j.status === "in_progress");
    const completedJobs = jobs.filter((j) => j.status === "completed");

    return (
      <div data-testid="mechanic-dashboard" className="space-y-6">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            My Dashboard
          </h1>
          <p className="text-slate-500">
            Welcome back, <span className="font-semibold">{user?.name}</span>
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            title="Pending Jobs"
            value={pendingJobs.length}
            icon={Clock}
            color="bg-amber-100 text-amber-700"
          />
          <StatCard
            title="In Progress"
            value={inProgressJobs.length}
            icon={Wrench}
            color="bg-blue-100 text-blue-700"
          />
          <StatCard
            title="Completed"
            value={completedJobs.length}
            icon={CheckCircle}
            color="bg-green-100 text-green-700"
          />
        </div>

        {/* Pending Jobs */}
        {pendingJobs.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
              <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Pending Jobs ({pendingJobs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {pendingJobs.map((job) => (
                <div key={job.id} className="p-4 bg-amber-50 rounded-sm border border-amber-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {getStatusBadge(job.status)}
                      <h3 className="font-heading font-bold text-lg text-slate-900 mt-2">
                        {job.description}
                      </h3>
                      <div className="text-sm text-slate-600 mt-2 space-y-1">
                        <p><strong>Customer:</strong> {job.customer_name}</p>
                        <p><strong>Excavator:</strong> {job.excavator_model} - {job.excavator_serial}</p>
                        {job.location_address && (
                          <p className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {job.location_address}
                          </p>
                        )}
                      </div>
                    </div>
                    <Link to={`/jobs/${job.id}`}>
                      <Button variant="outline" className="rounded-sm">
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* In Progress Jobs */}
        {inProgressJobs.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
              <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                <Wrench className="w-5 h-5 text-blue-500" />
                In Progress ({inProgressJobs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {inProgressJobs.map((job) => (
                <div key={job.id} className="p-4 bg-blue-50 rounded-sm border border-blue-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {getStatusBadge(job.status)}
                      <h3 className="font-heading font-bold text-lg text-slate-900 mt-2">
                        {job.description}
                      </h3>
                      <div className="text-sm text-slate-600 mt-2 space-y-1">
                        <p><strong>Customer:</strong> {job.customer_name}</p>
                        <p><strong>Excavator:</strong> {job.excavator_model} - {job.excavator_serial}</p>
                        {job.check_in && (
                          <p className="text-blue-600">
                            Checked in: {formatDateTime(job.check_in.timestamp)}
                          </p>
                        )}
                      </div>
                    </div>
                    <Link to={`/jobs/${job.id}`}>
                      <Button className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm">
                        <Eye className="w-4 h-4 mr-2" />
                        Continue
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Completed Jobs */}
        {completedJobs.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
              <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Completed ({completedJobs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {completedJobs.slice(0, 5).map((job) => (
                <div key={job.id} className="p-4 bg-green-50 rounded-sm border border-green-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {getStatusBadge(job.status)}
                      <h3 className="font-heading font-bold text-lg text-slate-900 mt-2">
                        {job.description}
                      </h3>
                      <p className="text-sm text-slate-600 mt-1">
                        {job.customer_name} • {job.excavator_model}
                      </p>
                    </div>
                    <Link to={`/jobs/${job.id}`}>
                      <Button variant="outline" className="rounded-sm">
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {jobs.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Wrench className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No jobs assigned to you yet</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Admin/Manager Dashboard
  return (
    <div data-testid="dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Dashboard
          </h1>
          <p className="text-slate-500">
            Welcome back, <span className="font-semibold">{user?.name}</span>
          </p>
        </div>
        {hasPermission("jobs") && (
          <Link to="/jobs">
            <Button className="bg-blue-500 text-black font-bold uppercase tracking-wider hover:bg-blue-600 rounded-sm border-2 border-transparent hover:border-black">
              <Wrench className="w-4 h-4 mr-2" />
              Create New Job
            </Button>
          </Link>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Pending Jobs"
          value={stats?.jobs?.pending || 0}
          icon={Clock}
          color="bg-amber-100 text-amber-700"
        />
        <StatCard
          title="In Progress"
          value={stats?.jobs?.in_progress || 0}
          icon={Wrench}
          color="bg-blue-100 text-blue-700"
        />
        <StatCard
          title="Completed"
          value={stats?.jobs?.completed || 0}
          icon={CheckCircle}
          color="bg-green-100 text-green-700"
        />
        <StatCard
          title="Total Revenue"
          value={`$${(stats?.revenue?.total || 0).toLocaleString()}`}
          icon={DollarSign}
          color="bg-yellow-100 text-yellow-700"
          subtitle={`$${(stats?.revenue?.pending || 0).toLocaleString()} pending`}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Customers"
          value={stats?.counts?.customers || 0}
          icon={Users}
          color="bg-slate-100 text-slate-700"
        />
        <StatCard
          title="Excavators"
          value={stats?.counts?.excavators || 0}
          icon={Truck}
          color="bg-slate-100 text-slate-700"
        />
        <StatCard
          title="Parts in Stock"
          value={stats?.counts?.parts || 0}
          icon={Package}
          color="bg-slate-100 text-slate-700"
        />
        <StatCard
          title="Pending Invoices"
          value={stats?.invoices?.pending || 0}
          icon={FileText}
          color="bg-purple-100 text-purple-700"
        />
      </div>

      {/* Low Stock Alert */}
      {stats?.low_stock_parts?.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-tight flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5" />
              Low Stock Alert
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {stats.low_stock_parts.slice(0, 6).map((part) => (
                <div
                  key={part.id}
                  className="bg-white p-3 rounded-sm border border-amber-200 flex items-center justify-between"
                >
                  <div>
                    <p className="font-semibold text-slate-900">{part.name}</p>
                    <p className="text-xs text-slate-500">{part.part_number}</p>
                  </div>
                  <span className="font-heading font-bold text-xl text-amber-600">
                    {part.quantity}
                  </span>
                </div>
              ))}
            </div>
            {hasPermission("parts") && (
              <Link to="/parts" className="mt-4 inline-flex items-center text-amber-700 hover:text-amber-900 font-semibold text-sm">
                View all inventory <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {hasPermission("jobs") && (
          <Link to="/jobs" className="block">
            <Card className="border-slate-200 hover:border-blue-500 hover:bg-yellow-50 transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 bg-slate-800 rounded-sm">
                  <Wrench className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="font-heading font-bold uppercase text-slate-900">View Jobs</p>
                  <p className="text-sm text-slate-500">Manage repair work</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}

        {hasPermission("customers") && (
          <Link to="/customers" className="block">
            <Card className="border-slate-200 hover:border-blue-500 hover:bg-yellow-50 transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 bg-slate-800 rounded-sm">
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="font-heading font-bold uppercase text-slate-900">Customers</p>
                  <p className="text-sm text-slate-500">Manage clients</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}

        {hasPermission("excavators") && (
          <Link to="/excavators" className="block">
            <Card className="border-slate-200 hover:border-blue-500 hover:bg-yellow-50 transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 bg-slate-800 rounded-sm">
                  <Truck className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="font-heading font-bold uppercase text-slate-900">Excavators</p>
                  <p className="text-sm text-slate-500">Equipment registry</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}

        {hasPermission("parts") && (
          <Link to="/parts" className="block">
            <Card className="border-slate-200 hover:border-blue-500 hover:bg-yellow-50 transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 bg-slate-800 rounded-sm">
                  <Package className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="font-heading font-bold uppercase text-slate-900">Inventory</p>
                  <p className="text-sm text-slate-500">Parts & supplies</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
